import { setStepflowAccessToken } from './apiToken';
import { setServerPlaces } from '../utils/lastPlace.js';
import { normalizeStepflowRole } from '../utils/stepflowRoles';

const LS_TENANT = 'stepflowActiveTenantId';
/** Last workspace the user explicitly switched to (persists across sessions). */
const LS_LAST_USER_WORKSPACE = 'stepflowLastUserWorkspaceId';

export function getActiveTenantId() {
  return localStorage.getItem(LS_TENANT) || '';
}

export function setActiveTenantId(tid) {
  if (tid) {
    localStorage.setItem(LS_TENANT, tid);
  } else {
    localStorage.removeItem(LS_TENANT);
  }
}

export function getLastUserWorkspaceId() {
  try {
    return localStorage.getItem(LS_LAST_USER_WORKSPACE) || '';
  } catch {
    return '';
  }
}

export function setLastUserWorkspaceId(tid) {
  try {
    if (tid) localStorage.setItem(LS_LAST_USER_WORKSPACE, tid);
    else localStorage.removeItem(LS_LAST_USER_WORKSPACE);
  } catch {
    /* ignore */
  }
}

export function clearWorkspaceLocalPreferences() {
  try {
    localStorage.removeItem(LS_TENANT);
    localStorage.removeItem(LS_LAST_USER_WORKSPACE);
  } catch {
    /* ignore */
  }
}

/**
 * Pick active tenant after login: prefer last explicit workspace choice, then avoid landing on an OWNER-only
 * default when the user also has invited (non-OWNER) memberships — reduces unwanted pricing gate on load.
 */
export function pickInitialWorkspaceTenantId(list, cognitoTenantId, serverLastTenantId = '') {
  const active = (list || []).filter((w) => (w.status || 'ACTIVE') === 'ACTIVE');
  const activeIds = new Set(active.map((w) => w.tenantId));

  // THE LAST WORKSPACE the person worked in, remembered on the SERVER (every session mint) — so a sign-in on any
  // device, or after logout, opens where they left off
  const serverLast = String(serverLastTenantId || '').trim();
  if (serverLast && activeIds.has(serverLast)) {
    return serverLast;
  }

  const lastUser = (getLastUserWorkspaceId() || '').trim();
  if (lastUser && activeIds.has(lastUser)) {
    return lastUser;
  }

  const saved = (getActiveTenantId() || '').trim();
  if (saved && activeIds.has(saved)) {
    const row = active.find((w) => w.tenantId === saved);
    const role = normalizeStepflowRole(row?.role);
    const hasNonOwner = active.some((w) => normalizeStepflowRole(w.role) !== 'OWNER');
    if (role === 'OWNER' && hasNonOwner) {
      const non = active.find((w) => normalizeStepflowRole(w.role) !== 'OWNER');
      if (non?.tenantId) return non.tenantId;
    }
    return saved;
  }

  const nonOwner = active.find((w) => normalizeStepflowRole(w.role) !== 'OWNER');
  if (nonOwner?.tenantId) return nonOwner.tenantId;

  const first = active[0];
  return first?.tenantId || (list && list[0]?.tenantId) || cognitoTenantId || '';
}

export async function fetchWorkspaces(idToken, apiBase) {
  const r = await fetch(`${apiBase}/auth/workspaces`, {
    headers: { Authorization: `Bearer ${idToken}` },
  });
  if (!r.ok) {
    const t = await r.text();
    throw new Error(t || r.statusText);
  }
  const data = await r.json();
  setServerPlaces(data.lastPlaces);          // where you left off in each workspace (§8a) — every caller refreshes it
  return {
    workspaces: data.workspaces || [],
    pendingInvites: data.pendingInvites || [],
    onboarding: data.onboarding || null,
    justAdded: data.justAdded || [],
    lastTenantId: data.lastTenantId || '',
    lastPlaces: data.lastPlaces || {},
  };
}

export async function exchangeWorkspaceSession(idToken, tenantId, apiBase) {
  const r = await fetch(`${apiBase}/auth/session`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ tenantId }),
  });
  if (!r.ok) {
    const t = await r.text();
    throw new Error(t || r.statusText);
  }
  return r.json();
}

/**
 * After Cognito login: list workspaces, pick tenant, mint StepFlow REST token.
 */
export async function bootstrapWorkspaceRestSession({
  idToken,
  apiBase,
  cognitoTenantId,
}) {
  let list = [];
  let pendingInvites = [];
  let justAdded = [];        // workspaces an admin ADDED this person to, just now (Inbox FYI)
  let onboarding = null;     // the resolver's verdict (LOGIN-TO-TENANT.md §3): ready | invited | create | invite_only
  let lastTenantId = '';     // the server's record of where this person last worked
  try {
    const fetched = await fetchWorkspaces(idToken, apiBase);
    list = fetched.workspaces || [];
    pendingInvites = fetched.pendingInvites || [];
    onboarding = fetched.onboarding || null;
    justAdded = fetched.justAdded || [];
    lastTenantId = fetched.lastTenantId || '';
  } catch (e) {
    console.warn('auth/workspaces failed', e);
  }

  const activeIds = new Set(
    (list || []).filter((w) => (w.status || 'ACTIVE') === 'ACTIVE').map((w) => w.tenantId)
  );

  let tid = pickInitialWorkspaceTenantId(list, cognitoTenantId, lastTenantId);
  if (tid && !activeIds.has(tid)) {
    const active = (list || []).filter((w) => (w.status || 'ACTIVE') === 'ACTIVE');
    tid = active[0]?.tenantId || '';
  }

  if (!tid) {
    setStepflowAccessToken(null);
    return {
      workspaces: list,
      pendingInvites,
      onboarding,
      justAdded,
      tenantId: null,
      role: null,
    };
  }

  const data = await exchangeWorkspaceSession(idToken, tid, apiBase);
  setStepflowAccessToken(data.accessToken);
  setActiveTenantId(tid);
  const oid = (data.orgId || '').trim();
  return {
    workspaces: list,
    pendingInvites,
    onboarding,
    justAdded,
    tenantId: tid,
    role: data.role || null,
    orgId: oid || undefined,
  };
}
